import React, { useState, useEffect, useRef } from 'react';
import './MessageBubble.scss';

const MessageBubble = ({ message, isOwn, showTime, unreadParticipantCount = 0 }) => {
  // 모달 상태 관리
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // 확대/축소 상태 관리
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialDistance, setInitialDistance] = useState(0);
  const [initialScale, setInitialScale] = useState(1);
  const [pinchCenter, setPinchCenter] = useState({ x: 0, y: 0 });
  const [isPinching, setIsPinching] = useState(false);
  
  // 이미지 컴테이너 ref
  const imageContainerRef = useRef(null);

  // 메시지 시간 포맷
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const messageDate = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return messageDate.toLocaleTimeString('ko-KR', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // 메시지 타입에 따른 렌더링
  const renderMessageContent = () => {
    switch (message.messageType) {
      case 'image':
        return (
          <div className="message-image-container">
            {/* 이미지 데이터가 있는 경우 */}
            {message.images && message.images.length > 0 ? (
              <div className={`message-images ${message.images.length === 1 ? 'single' : 'multiple'}`}>
                {message.images.map((image, index) => (
                  <div key={index} className="image-wrapper">
                    <img 
                      src={image.url} 
                      alt={`Image ${index + 1}`}
                      onClick={() => handleImageClick(image.url)}
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            ) : (
              /* images 속성이 없는 경우 - 임시로 플레이스홀더 표시 */
              <div className="message-image-placeholder">
                <div className="placeholder-content">
                  <span>🖼️ 이미지</span>
                  <small>이미지 데이터를 불러올 수 없습니다</small>
                </div>
              </div>
            )}
            
            {/* 이미지와 함께 전송된 텍스트 메시지 */}
            {(message.text || message.message) && (message.text || message.message).trim() && (
              <div className="image-message-text">
                {renderTextWithLinks(message.text || message.message)}
              </div>
            )}
          </div>
        );
      
      case 'file':
        return (
          <div className="message-file">
            <div className="file-icon">
              {message.fileData?.type?.startsWith('image/') ? '🖼️' :
                message.fileData?.type?.includes('pdf') ? '🖼️' :
                message.fileData?.type?.includes('word') ? '📝' :
                message.fileData?.type?.includes('zip') ? '🗜️' : '📄'}
            </div>
            <div className="file-info">
              <span className="file-name">{message.text || message.message}</span>
              {message.fileData?.size && (
                <span className="file-size">
                용량: {(message.fileData.size / 1024 / 1024).toFixed(2)} MB
                </span>
              )}
              {message.fileData?.url && (
                <a 
                  href={message.fileData.url} 
                  download={message.fileData.name || (message.text || message.message)}
                  className="download-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  다운로드
                </a>
              )}
            </div>
          </div>
        );
      
      case 'system':
        return (
          <div className="system-message">
            {message.text || message.message}
          </div>
        );
      
      default:
        return (
          <div className="message-text">
            {/* 텍스트에서 링크를 감지하여 클릭 가능하게 만들기 */}
            {renderTextWithLinks(message.text || message.message)}
          </div>
        );
    }
  };

  // 이미지 클릭 핸들러 (모달로 확대보기)
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsModalOpen(true);
    // 확대/축소 상태 초기화
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // 모달 닫기
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
    // 확대/축소 상태 초기화
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // 모달 배경 클릭 시 닫기
  const handleModalBackgroundClick = (e) => {
    if (e.target === e.currentTarget && scale === 1) {
      closeModal();
    }
  };
  
  // 터치 시작 (핀치 줌용)
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      
      // 핀치 거리 계산
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      
      // 핀치 중심점 계산
      const rect = e.currentTarget.getBoundingClientRect();
      const centerX = (touch1.clientX + touch2.clientX) / 2 - rect.left;
      const centerY = (touch1.clientY + touch2.clientY) / 2 - rect.top;
      
      setInitialDistance(distance);
      setInitialScale(scale);
      setPinchCenter({ x: centerX, y: centerY });
      setIsPinching(true);
    } else if (e.touches.length === 1 && scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    }
  };
  
  // 터치 이동 (핀치 줌 및 드래그)
  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && isPinching && initialDistance > 0) {
      e.preventDefault();
      
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      
      // 현재 핀치 거리
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      
      // 새로운 스케일 계산
      const scaleChange = distance / initialDistance;
      const newScale = Math.max(0.8, Math.min(4, initialScale * scaleChange));
      
      setScale(newScale);
      
    } else if (e.touches.length === 1 && isDragging && scale > 1) {
      e.preventDefault();
      
      const newX = e.touches[0].clientX - dragStart.x;
      const newY = e.touches[0].clientY - dragStart.y;
      
      setPosition({ x: newX, y: newY });
    }
  };
  
  // 터치 종료
  const handleTouchEnd = (e) => {
    setIsDragging(false);
    setInitialDistance(0);
    setPinchCenter({ x: 0, y: 0 });
    setIsPinching(false);
    
    // 스케일이 너무 작으면 자동으로 리셋
    if (scale < 1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  };
  
  // 마우스 휠로 확대/축소 (데스크톱용)
  const handleWheel = (e) => {
    e.preventDefault();
    
    const delta = e.deltaY > 0 ? 0.95 : 1.05; // 더 부드러운 스케일 변화
    const newScale = Math.max(1, Math.min(4, scale * delta));
    
    // 마우스 위치를 기준으로 확대/축소
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    // 마우스 위치 기준 오프셋 계산
    const offsetX = (mouseX - centerX) * (newScale - scale) / scale;
    const offsetY = (mouseY - centerY) * (newScale - scale) / scale;
    
    setScale(newScale);
    
    if (newScale === 1) {
      setPosition({ x: 0, y: 0 });
    } else {
      setPosition({
        x: position.x - offsetX,
        y: position.y - offsetY
      });
    }
  };
  
  // wheel 이벤트 리스너 등록 (passive: false로 설정)
  useEffect(() => {
    const container = imageContainerRef.current;
    if (container && isModalOpen) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      
      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }
  }, [isModalOpen, scale, position]); // 의존성 배열에 필요한 값들 추가

  // 마우스 드래그 핸들러들
  const handleMouseDown = (e) => {
    if (scale > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      e.preventDefault();
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      setPosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 마우스 및 터치 이벤트 리스너 등록 (passive: false로 설정)
  useEffect(() => {
    const container = imageContainerRef.current;
    if (container && isModalOpen) {
      const handleMouseMoveGlobal = (e) => handleMouseMove(e);
      const handleMouseUpGlobal = () => handleMouseUp();
      const handleTouchMoveGlobal = (e) => handleTouchMove(e);
      const handleTouchEndGlobal = (e) => handleTouchEnd(e);

      // 마우스 이벤트
      container.addEventListener('mousedown', handleMouseDown, { passive: false });
      document.addEventListener('mousemove', handleMouseMoveGlobal, { passive: false });
      document.addEventListener('mouseup', handleMouseUpGlobal);
      
      // 터치 이벤트
      container.addEventListener('touchstart', handleTouchStart, { passive: false });
      container.addEventListener('touchmove', handleTouchMoveGlobal, { passive: false });
      container.addEventListener('touchend', handleTouchEndGlobal, { passive: false });
      
      return () => {
        // 마우스 이벤트 제거
        container.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mousemove', handleMouseMoveGlobal);
        document.removeEventListener('mouseup', handleMouseUpGlobal);
        
        // 터치 이벤트 제거
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMoveGlobal);
        container.removeEventListener('touchend', handleTouchEndGlobal);
      };
    }
  }, [isModalOpen, isDragging, scale, position, dragStart, isPinching, initialDistance, initialScale]);

  // 텍스트에서 URL, 이메일, 도메인을 감지하여 링크로 변환
  const renderTextWithLinks = (text) => {
    // 통합 정규식으로 모든 링크 패턴을 찾기
    const linkRegex = /(https?:\/\/[^\s]+)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})|([a-zA-Z0-9-]+\.[a-zA-Z0-9.-]*[a-zA-Z]{2,})/g;
    
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
      // 매치 이전의 텍스트 추가
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      
      const fullMatch = match[0];
      const url = match[1];
      const email = match[2];
      const domain = match[3];
      
      if (url) {
        // URL
        parts.push({
          type: 'url',
          content: fullMatch
        });
      } else if (email) {
        // 이메일
        parts.push({
          type: 'email',
          content: fullMatch
        });
      } else if (domain && !email) {
        // 도메인 (이메일이 아닌 경우)
        parts.push({
          type: 'domain',
          content: fullMatch
        });
      } else {
        parts.push(fullMatch);
      }
      
      lastIndex = match.index + fullMatch.length;
    }
    
    // 남은 텍스트 추가
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    
    return parts.map((part, index) => {
      if (typeof part === 'object') {
        switch (part.type) {
          case 'url':
            return (
              <a 
                key={index} 
                href={part.content} 
                target="_blank" 
                rel="noopener noreferrer"
                className="message-link"
              >
                {part.content}
              </a>
            );
          case 'email':
            return (
              <a 
                key={index} 
                href={`mailto:${part.content}`}
                className="email-link"
              >
                {part.content}
              </a>
            );
          case 'domain':
            return (
              <a 
                key={index} 
                href={`https://${part.content}`}
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  color: '#0066cc',
                  textDecoration: 'underline',
                  textDecorationColor: '#0066cc'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = '#0052a3';
                  e.target.style.textDecorationColor = '#0052a3';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#0066cc';
                  e.target.style.textDecorationColor = '#0066cc';
                }}
                className="message-link"
              >
                {part.content}
              </a>
            );
          default:
            return part.content;
        }
      }
      return part;
    });
  };

  // 시스템 메시지인 경우 특별한 스타일 적용
  if (message.messageType === 'system') {
    return (
      <div className="message-bubble system">
        {renderMessageContent()}
      </div>
    );
  }

  return (
    <>
      <div className={`message-bubble ${isOwn ? 'own' : 'other'}`}>
        <div className={`message-content ${message.messageType === 'image' ? 'image-message' : ''} ${message.messageType === 'file' ? 'file-message' : ''}`}>
          {renderMessageContent()}
        </div>
        
        {showTime && (
          <div className="message-time">
            {isOwn && unreadParticipantCount > 0 && (
              <span className="message-status">
                <span className="read-status">{unreadParticipantCount}</span>
              </span>
            )}
            {formatMessageTime(message.timestamp)}
          </div>
        )}
      </div>

      {/* 이미지 모달 */}
      {isModalOpen && selectedImage && (
        <div className="image-modal-overlay" onClick={handleModalBackgroundClick}>
          <div className="image-modal-content">
            <button className="modal-close-btn" onClick={closeModal}>
              ×
            </button>
            <div 
              ref={imageContainerRef}
              className="image-container"
            >
              <img 
                src={selectedImage} 
                alt="확대된 이미지" 
                className="modal-image"
                style={{
                  transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                  transformOrigin: 'center center'
                }}
                draggable={false}
              />
            </div>
            {/* {scale > 1 && (
              <div className="zoom-info">
                확대됨 · 핀치하여 원래 크기로
              </div>
            )} */}
          </div>
        </div>
      )}
    </>
  );
};

// React.memo로 불필요한 리렌더링 방지
export default React.memo(MessageBubble);
